/**
 * Created by Ning Jiang on 2021-05-04.
 */
import {KarenParsedData} from "./KarenParsedData";
import {IBetWayWinData, IBetWinData, IKarenData, IKarenGameData, IStickyFS} from "./IKarenData";
import {KarenDataParser} from "./KarenDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {KarenGameConfig} from "../../KarenGameConfig";
import {Karen} from "../../Karen";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {GameHelper} from "../../../core/utils/GameHelper";


export class KarenParsedGameData extends KarenParsedData {

    public readonly betWins: IBetWinData[];
    public readonly singleWin: number;
    public readonly playedBetValue: number;
    public readonly wasFeatureBuy: boolean;
    public readonly brokeTheBank: boolean;
    public readonly symbolSizes: number[][];
    public readonly mysterySymbolReveal: string;
    // Only sends in the last freespin.

    // Calculated values
    public readonly currentWinRatioConfig: IWinRatioConfig | null;

    // To add Spin Data
    public readonly nearWinReels: boolean[];
    public readonly nearWinBigWinReels: boolean[];

    // To add Nudge Data
    public readonly doReelNudge: boolean;
    public readonly xWPositions: number[];
    public readonly xWInfPositions: number[][];
    public readonly xWayNumbersOnEachReel: number[];
    public readonly numberOfSymbolsOnReel: number[];
    public readonly finalXWayNumbersOnEachReel: number[];
    public readonly xWayNumbersOnEachNudgeStep: number;
    public readonly stickyXWaysFSPositions: IStickyFS;
    public readonly fsXWaysSize: number;
    public readonly boostedBetPlayed: boolean;
    public readonly totalNumberOfCombinations: number;
    public readonly highlightPotentialWin: boolean[][];
    public highlightPotentialWin1: boolean[][];
    public highlightPotentialWin2: boolean[][];
    public readonly winBelowStack: boolean;
    public isStickyFSParticipatingWin: boolean;
    public symbolWinCombinaionEachReel: number[];
    public isWildParticipatingInWin: boolean;
    public XWParticipatingInWin:boolean[];


    // To add Avalanche Data:
    // public readonly newSymbols:string[][];
    // public readonly nearWinPositions:boolean[][];

    constructor(rawData: IKarenGameData, parser: KarenDataParser) {
        super(rawData, parser);
        this.betWins = parser.parseBetWins(rawData);
        this.singleWin = parseFloat(rawData.totalSpinWinnings);
        this.playedBetValue = parseFloat(rawData.playedBetValue);
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.boostedBetPlayed = rawData.boostedBetPlayed;
        this.brokeTheBank = rawData.brokeTheBank;
        // Calculated values.
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue);
        // To add Nudge Data only on last reel;
        this.doReelNudge = parser.calculateReelNudge(rawData);
        // To add Spin Data
        this.nearWinReels = rawData.nearWinReels == null ? this.parseNearWinReels() : rawData.nearWinReels.concat();
        this.nearWinBigWinReels = rawData.nearWinBigWinReels == null ? ArrayHelper.initArrayWithValues(KarenGameConfig.REELS_NUM, () => false) : rawData.nearWinBigWinReels.concat();
        this.symbolSizes = rawData.symbolSizes;
        this.mysterySymbolReveal = rawData.mysterySymbolReveal;
        this.xWPositions = KarenDataParser.parseXWPositions(rawData);
        this.xWInfPositions = KarenDataParser.parseXWInfPositions(rawData);
        (<IBetWayWinData[]>this.betWins!).forEach((betWins: IBetWayWinData) => {
            betWins.reelWinPositions = this.calculateReelWinPos(rawData, betWins.symbol);
        });
        this.numberOfSymbolsOnReel = this.calculateNumberOfSymbolsOnReel(rawData);
        this.xWayNumbersOnEachReel = this.calculateXWaysEachReels(rawData);
        this.finalXWayNumbersOnEachReel = this.calculateFinalXWayNumbersOnEachReel(rawData);
        this.xWayNumbersOnEachNudgeStep = this.calculateXWaysOnEachNudgeStep(rawData);
        this.totalNumberOfCombinations = this.getTotalNumberOfCombination(rawData);
        this.fsXWaysSize = rawData.fsXWaysSizeBefore;
        this.stickyXWaysFSPositions = rawData.stickyXWaysFSPositions;
        this.highlightPotentialWin = this.calculatePotentialWin(rawData);
        if (GameHelper.isFreeSpin(rawData.mode)) {
            this.isStickyFSParticipatingWin = this.calculateWinPositionOnStickyFS(rawData);
            if (this.isStickyFSParticipatingWin) {
                this.symbolWinCombinaionEachReel = this.calculateSymbolWinCombinationEachReel(rawData);
            }
        }
        let enhancedBetWin: boolean = false;
        if (rawData.boostedBetPlayed) {
            enhancedBetWin = this.singleWin <= ( this.playedBetValue + this.playedBetValue * 0.30 );
        }
        this.winBelowStack = enhancedBetWin || ( Karen.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && ( this.singleWin <= this.playedBetValue ) );
        //todo remove  winBelowStack and use gameheloper instead
        this.isWildParticipatingInWin = this.calculateIsWildParticipatingWin();
        this.XWParticipatingInWin = this.calculateWinPositionOnXW(rawData);
    }

    public calculateIsWildParticipatingWin(): boolean {
        let isWildWin: boolean = false;
        if (this.betWins.length > 0) {
            (<IBetWayWinData[]>this.betWins!).forEach((betWins: IBetWayWinData) => {
                if (betWins.length === 6) {
                    isWildWin = true;
                }
            });
        }
        return isWildWin
    }

    public calculateWinPositionOnStickyFS(rawData: IKarenGameData): boolean {
        let isWinOnStcky: boolean = false;
        this.stickyFSPositions.forEach((symId, reelId) => {
            if (symId > -1) {
                (<IBetWayWinData[]>this.betWins!).forEach((betWins: IBetWayWinData) => {
                    if (betWins.reelWinPositions[reelId][symId]) {
                        isWinOnStcky = true;
                    }
                });
            }
        });
        return isWinOnStcky;
    }

    public calculateWinPositionOnXW(rawData: IKarenGameData): boolean[] {
        let XWWinBool:boolean[]=GameHelper.createSingleArray();
        this.scatterPositions!.forEach((symId, reelId) => {
            if (symId > -1) {
                (<IBetWayWinData[]>this.betWins!).forEach((betWins: IBetWayWinData) => {
                    if (betWins.reelWinPositions[reelId][symId]) {
                        XWWinBool[reelId] = true;
                    }
                });
            }
        });
        this.xWPositions!.forEach((symId, reelId) => {
            if (symId > -1) {
                (<IBetWayWinData[]>this.betWins!).forEach((betWins: IBetWayWinData) => {
                    if (betWins.reelWinPositions[reelId][symId]) {
                        XWWinBool[reelId] = true;
                    }
                });
            }
        });
        return XWWinBool;
    }

    protected parseScatterPositions(rawData: IKarenGameData): number[] {
        const scatterPos: number[] = [];
        for (let reelId: number = 0; reelId < 6; reelId++) {
            for (let row: number = 1; row <= rawData.evaluatedArea[reelId].length; row++) {
                if (rawData.reels[reelId][row] === "S") {
                    scatterPos[reelId] = row - 1;
                    break;
                } else {
                    scatterPos[reelId] = -1;
                }
            }
        }
        return scatterPos;
    }

    private checkForinfectiousOnFirstReel(rawData: IKarenGameData, isInfXW: boolean, toSConverted: boolean, potentArr: boolean[][]): boolean[][] {
        if (isInfXW && ( ( this.xWPositions[2] > -1 ) || ( toSConverted && ( this.scatterPositions![1] > -1 || this.scatterPositions![2] > -1 ) ) )) {
            potentArr = this.highLightSecnd(rawData, potentArr, toSConverted);
        }
        if (isInfXW && ( ( this.xWPositions[1] > -1 ) || ( toSConverted && ( this.scatterPositions![1] > -1 || this.scatterPositions![2] > -1 ) ) )) {
            potentArr = this.highLightThirdReel(rawData, potentArr, toSConverted);
        }
        return potentArr;
    }

    private createFalsyArray(rawData: IKarenGameData): boolean[][] {
        return ArrayHelper.initArrayWithValues(KarenGameConfig.REELS_NUM, (index: number) => ArrayHelper.initArrayWithValues(rawData.evaluatedArea[index].length, () => false));
    }

    private calculatePotentialWin(rawData: IKarenGameData): boolean[][] {
        let isInFSMode: boolean = ( rawData.mode === GameMode.FREESPIN );
        let potentArr: boolean[][] = this.createFalsyArray(rawData);
        this.highlightPotentialWin1 = this.createFalsyArray(rawData);
        this.highlightPotentialWin2 = this.createFalsyArray(rawData);
        let lastIndexOfScatter: number = -1;
        let lastIndexOfXW: number = -1;
        let potSymIndx: number = -1;
        const sCount: number = this.scatterPositions!.filter((value: number, index: number) => {
            if (value > -1) {
                lastIndexOfScatter = index;
            }
            return value > -1;
        }).length;
        const xwayCount: number = this.xWPositions!.filter((value: number, index: number) => {
            if (value > -1) {
                lastIndexOfXW = index;
            }
            return value > -1;
        }).length;
        let isInfXW: boolean = rawData.reels[0][1] === "XW2" || rawData.reels[0][2] == "XW2";
        let toSConverted: boolean = sCount < 3 && sCount > 0;
        let toBeHighlighting: boolean = xwayCount > 1 || ( toSConverted && ( sCount + xwayCount ) > 1 );
        if (toSConverted) {
            potSymIndx = lastIndexOfScatter > lastIndexOfXW ? lastIndexOfScatter : lastIndexOfXW;
        } else {
            potSymIndx = lastIndexOfXW;
        }
        if (isInFSMode) {
            for (let reelId = 0; reelId < 6; reelId++) {
                if (this.stickyXWaysFSPositions[reelId]) {
                    potSymIndx = this.stickyXWaysFSPositions[reelId];
                }
            }
        }
        if (toBeHighlighting || isInFSMode) { //as in #42
            const firstReelSyms: string[] = rawData.evaluatedArea[0];
            for (let symIndx: number = 0; symIndx < firstReelSyms.length; symIndx++) {
                const winSym: string = firstReelSyms[symIndx];
                if (!isInFSMode) {
                    potentArr = this.testConnectingSymbols(rawData, winSym, toSConverted, potSymIndx, potentArr, isInfXW);
                } else {
                    if (symIndx == 0) {
                        this.highlightPotentialWin1 = this.testConnectingSymbols(rawData, winSym, toSConverted, potSymIndx, this.createFalsyArray(rawData), isInfXW);
                    } else if (symIndx == 1) {
                        this.highlightPotentialWin2 = this.testConnectingSymbols(rawData, winSym, toSConverted, potSymIndx, this.createFalsyArray(rawData), isInfXW);
                    }
                }
            }
        }
        this.checkForinfectiousOnFirstReel(rawData, isInfXW, toSConverted, potentArr);
        return potentArr
    }

    /*
    * highlight all pontential winning symbols, which means all symbols that appear on both reel 2 and 3. And remember if there is a xWays on one of those reels it can turn into everything
    * */
    private highLightSecnd(rawData: IKarenData, potentArr: boolean[][], toSConverted: boolean): boolean[][] {
        for (let reelIndex: number = 1; reelIndex <= 2; reelIndex++) {
            for (let symIndex: number = 0; symIndex < potentArr[reelIndex].length; symIndex++) {
                if (!( rawData.reels[reelIndex][symIndex + 1].indexOf("XW") > -1 ) && !( ( rawData.reels[reelIndex][symIndex + 1].indexOf("S") > -1 && toSConverted ) )) {
                    potentArr[reelIndex][symIndex] = true;
                }
            }
        }
        return potentArr;
    }

    private highLightThirdReel(rawData: IKarenData, potentArr: boolean[][], toSConverted: boolean): boolean[][] {
        for (let reelIndex: number = 2; reelIndex <= 2; reelIndex++) {
            for (let symIndex: number = 0; symIndex < potentArr[reelIndex].length; symIndex++) {
                if (!( rawData.reels[reelIndex][symIndex + 1].indexOf("XW") > -1 ) && !( ( rawData.reels[reelIndex][symIndex + 1].indexOf("S") > -1 && toSConverted ) )) {
                    potentArr[reelIndex][symIndex] = true;
                }
            }
        }
        return potentArr;
    }

    private testStickyFSPos(reelId: number, symId: number): boolean {
        let isPossible: boolean = false;
        if (this.stickyXWaysFSPositions[reelId] == (symId-1)) {
            isPossible = true;
        }
        return isPossible;
    }

    private testConnectingSymbols(rawData: IKarenData, winSym: string, toSConverted: boolean, potSymIndx: number, potentArr: boolean[][], isInfXW: boolean): boolean[][] {
        let connectedReel: number = 1;
        let checkExisting: boolean = false;
        let winningReels: boolean[] = ArrayHelper.initArrayWithValues(KarenGameConfig.REELS_NUM, () => false);
        for (let reelIndex: number = 1; reelIndex < rawData.reels.length; reelIndex++) {
            for (let symInd: number = 1; symInd <= rawData.evaluatedArea[reelIndex].length; symInd++) {
                if (rawData.reels[reelIndex][symInd].indexOf(winSym) > -1 || ( rawData.reels[reelIndex][symInd].indexOf("XW") > -1 )
                    || this.testStickyFSPos(reelIndex, symInd)
                    || ( ( rawData.reels[reelIndex][symInd].indexOf("S") > -1 ) && toSConverted )
                ) {
                    checkExisting = true;
                }
            }
            if (checkExisting) {
                connectedReel++;
                winningReels[reelIndex] = true;
                if (reelIndex === 5) {
                    if (( ( isInfXW && connectedReel > 1 ) || ( connectedReel >= potSymIndx && connectedReel > 2 ) )) {
                        winningReels[0] = true;
                        this.highLightWinningSyms(winSym, rawData, potentArr, winningReels);
                    }
                }
            } else {
                if (( ( isInfXW && connectedReel > 1 ) || ( connectedReel >= potSymIndx && connectedReel > 2 ) )) {
                    winningReels[0] = true;
                    this.highLightWinningSyms(winSym, rawData, potentArr, winningReels);
                }
                break;

            }
            checkExisting = false;
        }

        return potentArr;
    }

    private highLightWinningSyms(winSym: string, rawData: IKarenData, potentArr: boolean[][], winningReels: boolean[]): void {
        for (let reelIndex: number = 0; reelIndex < potentArr.length; reelIndex++) {
            for (let symIndex: number = 0; symIndex < potentArr[reelIndex].length; symIndex++) {
                if (rawData.reels[reelIndex][symIndex + 1].indexOf(winSym) > -1) {
                    if (winningReels[reelIndex]) {
                        potentArr[reelIndex][symIndex] = true;
                    }
                }
                if (( rawData.reels[reelIndex][symIndex] === "W" )) {
                    potentArr[reelIndex][potentArr[reelIndex].length - 1] = true;
                }
            }
        }
    }

    private parseNearWinReels(): boolean[] {
        let nearWinReels: boolean[] = GameHelper.createSingleArray();
        let sCount: number = 0;
        this.scatterPositions!.forEach((scatterPos: number, reel: number) => {
                if (sCount >= 2) {
                    nearWinReels[reel] = true;
                }
                if (scatterPos > -1) {
                    sCount++
                }
            }
        );
        if (KarenGameConfig.IS_FEATURE_BUY) {
            //#127
            if (sCount == 3) {
                nearWinReels[4] = false;
                nearWinReels[5] = false;
            } else if (sCount == 4) {
                nearWinReels = GameHelper.createSingleArray();
            }
            if (this.doReelNudge) {
                nearWinReels = GameHelper.createSingleArray();
                nearWinReels[4] = true;
                nearWinReels[5] = true;
            }
        } else {
            nearWinReels[5] = false;
        }
        return nearWinReels;
    }

    private calculateReelWinPos(rawData: IKarenGameData, symbol: string): boolean[][] {
        const reelWinPos: boolean[][] = ArrayHelper.initArrayWithValues<boolean[]>(KarenGameConfig.REELS_NUM, (index: number) => {
            return ArrayHelper.initArrayWithValues<boolean>(rawData.evaluatedArea[index].length, () => false);
        });
        let connectedReel: boolean = false;
        for (let reelId: number = 0; reelId < rawData.evaluatedArea.length; reelId++) {
            connectedReel = false;
            for (let symId: number = 0; symId < rawData.evaluatedArea[reelId].length; symId++) {
                if (rawData.evaluatedArea[reelId][symId] === symbol || rawData.evaluatedArea[reelId][symId] === "W") {
                    reelWinPos[reelId][symId] = true;
                    connectedReel = true;
                }
            }
            if (!connectedReel) {
                break;
            }
        }
        return reelWinPos;
    }

    private calculateXWaysEachReels(rawData: IKarenData): number[] {
        let xWayNumbers: number[] = ArrayHelper.initArrayWithValues(6, () => rawData.initialAreaSize.reduce((x, y) =>
            x * y));
        const numSyms: number[] = Object.assign([], rawData.initialAreaSize);
        this.checkXWCalculation(0, numSyms, xWayNumbers);
        return xWayNumbers;
    }

    private checkXWCalculation(currentIndex: number, numSyms: number[], xWayNumbers: number[]): number[] {
        let isScatter: boolean = false;
        let toFillIndex: number = currentIndex;
        let reelSize: number = numSyms[currentIndex] == 2 ? 3 : numSyms[currentIndex];
        for (let index: number = currentIndex; index < this.evaluatedArea.length; index++) {
            if (( index > 0 && this.scatterPositions![index] > -1 ) && !isScatter) {
                if (index !== 5) {
                    reelSize = this.evaluatedArea[index + 1].length;
                }
                isScatter = true;
                toFillIndex = index;
                continue;
            }
            if (isScatter) {
                numSyms[index] = reelSize;
            }
            if (index === this.evaluatedArea.length - 1) {
                if (toFillIndex + 1 <= index) {
                    let updatedInd: number = toFillIndex + 1;
                    while (updatedInd < 6) {
                        xWayNumbers[updatedInd] = numSyms.reduce((x, y) => x * y);
                        updatedInd++;
                    }
                }
                this.checkXWCalculation(toFillIndex + 1, numSyms, xWayNumbers)
            }
        }
        return xWayNumbers;
    }

    private calculateNumberOfSymbolsOnReel(rawData: IKarenGameData): number[] {
        const numberOfSyms: number[] = Object.assign([], rawData.initialAreaSize);
        rawData.symbolSizes.forEach((sizeArr: number[], index: number) => {
            numberOfSyms[index] = sizeArr.reduce(function (x, y) {
                return x + y;
            }, 0);
        });
        return numberOfSyms;
    }

    private calculateSymbolWinCombinationEachReel(rawData: IKarenGameData): number[] {
        let numberOfSyms = ArrayHelper.initArrayWithValues(KarenGameConfig.REELS_NUM, () => 0);
        let numberWithMult = ArrayHelper.initArrayWithValues(KarenGameConfig.REELS_NUM, () => 1);
        let numberWithMult2 = ArrayHelper.initArrayWithValues(KarenGameConfig.REELS_NUM, () => 1);
        for (let reelId: number = 0; reelId < rawData.evaluatedArea.length; reelId++) {
            let isConnectingReel: boolean = false;
            for (let symInd: number = 0; symInd < rawData.evaluatedArea[reelId].length; symInd++) {
                if (rawData.evaluatedArea[reelId][symInd] === rawData.mysterySymbolReveal) {
                    numberOfSyms[reelId]++;
                    isConnectingReel = true;
                }
            }
            if (!isConnectingReel) {
                break;
            }
        }
        let temp: number[] = Object.assign([], numberOfSyms);
        numberWithMult[0] = numberOfSyms.filter((value) => value > 0)!.reduce((x, y) => x * y);
        numberWithMult2[0] = numberWithMult[0];
        for (let reelId: number = 0; reelId < rawData.evaluatedArea.length; reelId++) {
            numberWithMult[reelId] = numberOfSyms[reelId];
            for (let symInd: number = 0; symInd < rawData.evaluatedArea[reelId].length; symInd++) {
                if (rawData.evaluatedArea[reelId][symInd] === rawData.mysterySymbolReveal) {
                    if (this.symbolSizes[reelId][symInd] !== 1) {
                        numberWithMult[reelId] += ( this.symbolSizes[reelId][symInd] - 1 );
                    }
                }
            }

            if (reelId > 0) {
                temp[reelId] = numberWithMult[reelId];
                numberWithMult2[reelId] = temp.filter((value) => value > 0).reduce((x, y) => x * y);
            }
            if (numberWithMult[reelId] === 0) {
                break;
            }
        }
        return numberWithMult2;
    }

    private calculateXWaysOnEachNudgeStep(rawData: IKarenGameData): number {
        const sum: number = rawData.symbolSizes[5].reduce(function (x, y) {
            return x + y;
        }, 0);
        return ( this.finalXWayNumbersOnEachReel[5] );
    }

    private calculateFinalXWayNumbersOnEachReel(rawData: IKarenGameData): number[] {
        const numberOfSyms: number[] = Object.assign([], rawData.initialAreaSize);
        const xWaysize: number = this.xWayNumbersOnEachReel[5];
        const finalxWays: number[] = ArrayHelper.initArrayWithValues(6, () => xWaysize);
        rawData.symbolSizes.forEach((sizeArr: number[], index: number) => {
            numberOfSyms[index] = sizeArr.reduce(function (x, y) {
                return x + y;
            }, 0);
            if (numberOfSyms[index] >= 3 && index <= 5 && index > 0) {
                finalxWays[index] = ( finalxWays[index - 1] / sizeArr.length ) * numberOfSyms[index];
            }
            if (index === 0) {
                finalxWays[index] = ( finalxWays[index] / sizeArr.length ) * numberOfSyms[index];
            }
        });
        return finalxWays;
    }

    private getTotalNumberOfCombination(rawData: IKarenGameData): number {
        let totalNumberOfCombination: number = 0;
        rawData.betWayWins?.forEach((betwayData: IBetWayWinData) => {
            totalNumberOfCombination += betwayData.numberOfCombinations;
        });
        return totalNumberOfCombination;
    }
}
